import { FeedbackSentiment } from '../feedback/types';

export enum ChatBotMessageRole {
  User = 'user',
  System = 'system',
}

export enum MessageState {
  Loading = 'LOADING',
  Fulfilled = 'FULFILLED',
  Error = 'ERROR',
}

export enum MessageType {
  Error = 'error',
  Message = 'message',
}

export enum ChartTypes {
  BAR = 'bar',
  PIE = 'pie',
  LINE = 'line',
}

export enum SupportedVisualizations {
  BAR = ChartTypes.BAR,
  PIE = ChartTypes.PIE,
  LINE = ChartTypes.LINE,
  TABLE = 'table',
}

export enum SupportedNodes {
  TEXT = 'text',
  TABLE = SupportedVisualizations.TABLE,
  BAR = SupportedVisualizations.BAR,
  PIE = SupportedVisualizations.PIE,
  LINE = SupportedVisualizations.LINE,
  NEXT_STEPS = 'next_steps',
  ERROR = 'error',
}

export enum ContentTypes {
  FUNCTION_CALL = 'FUNCTION_CALL',
}

export type TimeGranularity = 'day' | 'month' | 'quarter' | 'year';

export type VizEncoding = {
  x: {
    field: string;
    type: string;
    timeUnit: string;
  };
  y: {
    field: string;
    type: string;
    unit: string;
    stack: 'zero' | 'normalize';
  };
  color: {
    field: string;
    type: string;
    scale: {
      domain: Array<string>;
    };
  };
  theta: {
    type: string;
    field: string;
  }
};

export type VizBaseOptions = {
  vizTitle: string;
  mark: {
    type: ChartTypes;
    tooltip: boolean;
  };
  hints: {
    stack: boolean;
    supportedMarks: Array<ChartTypes>;
  };
  meta: {
    attribute_groupings: {
      sentiment_mentions: Array<string>;
      sentiment_percentages: Array<string>;
    };
    tool_call_id?: string;
    query_summary: {
      measures: Array<string>;
      filters: {};
      dimensions: {};
    };
  };
  encoding: VizEncoding;
};

export type VizBase = VizBaseOptions & {
  data: Array<Record<string, unknown>>;
};

export type TextNode = {
  type: SupportedNodes.TEXT;
  data: string;
};

export type TableNode = VizBase & {
  type: SupportedNodes.TABLE;
  // loading: boolean;
  // loadingText?: string;
};

export type ChartNode = VizBase & {
  type: ChartTypes;
  // loading: boolean;
};

export type NextStepsNode = {
  type: SupportedNodes.NEXT_STEPS;
  data: string[];
};

export type ErrorNode = {
  type: SupportedNodes.ERROR;
  message: string;
};

export type RichNodeType = ChartNode | TableNode | TextNode | NextStepsNode;

export type ContentNodes = Array<
  (ErrorNode & RichNodeType) | RichNodeType | ErrorNode
>;

export type BaseMessage = {
  id: string;
  role: ChatBotMessageRole;
  display_type?: MessageType;
  state?: MessageState;
  nodes: ContentNodes;
  createdAt?: string;
};

export type UserMessage = BaseMessage & {
  chatId?: string | null;
  role: ChatBotMessageRole.User;
  nodes: Array<TextNode>;
  executionId?: string | null;
};

type SystemMessageFeedback = {
  sentiment: FeedbackSentiment | null;
};

export type SystemMessage = BaseMessage & {
  role: ChatBotMessageRole.System;
  isResponseCopied: boolean;
  feedback?: SystemMessageFeedback | null;
  nodes: ContentNodes;
  chatId: string;
  chatPromptResponseId: string;
  executionId: string;
};

export type ActiveUserMessage = UserMessage & { chatId: string };
export type Message = SystemMessage | ActiveUserMessage;
export type Messages = Array<Message>;

export type Chat = {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
};

export type MessageFormData = {
  message: string;
  chatId?: string | null;
  currentDate?: string;
  communities?: Array<{ name: string; id: string }>;
  origin?: string;
};

export type MessagePayloadData = {
  message: string;
  chat_id?: string | null;
  current_date: string;
  communities?: Array<{ name: string; id: string }>;
  origin?: string;
};

export type StreamingState = 'INITIATED' | 'STREAMING' | 'FULFILLED';

export type StreamingUser = 'USER' | 'ASSISTANT' | 'FUNCTION';

export type PromptStreamOutput = {
  delta: string | null;
  id: string;
  name: 'prompt-stream';
  node_id: string;
  state: StreamingState;
  type: 'STRING';
  value: string;
};

export type DSLOutput = {
  delta: string | null;
  id: string;
  name: 'dsl-output';
  node_id: string;
  state: StreamingState;
  type: 'FUNCTION_CALL';
  value: {
    arguments: Record<string, any>;
    id: string;
    name: string;
    state: StreamingState;
  };
};

export type SuggestedNextStepsOutput = {
  id: string;
  name: 'final-chat-history';
  type: 'CHAT_HISTORY';
  value: {
    text: string;
    role: 'FUNCTION';
    content: {
      type: 'FUNCTION_CALL';
      value: {
        name: 'suggest_next_steps';
        arguments: {
          next_steps: string[];
        };
        id: string;
      };
    };
    source: null;
  }[];
};

export type ChatHistoryOutput = {
  delta: null;
  id: string;
  name: 'chat_history_final_output';
  node_id: string;
  state: StreamingState;
  type: 'CHAT_HISTORY';
  value: Array<{
    text: string;
    role: StreamingUser;
    source: null;
    content: {
      type: 'STRING' | 'ARRAY';
      value: string | Array<any>;
    };
  }>;
};

export type ChunkEventData = {
  error: string | null;
  id: string;
  inputs: Array<Record<string, unknown>>;
  output: (PromptStreamOutput | ChatHistoryOutput | DSLOutput | SuggestedNextStepsOutput) | null;
  outputs: Array<PromptStreamOutput | ChatHistoryOutput | DSLOutput | SuggestedNextStepsOutput> | null;
  state: StreamingState;
  ts: string;
};

export type ChunkEvent = {
  data: ChunkEventData;
  execution_id: string;
  run_id: string;
  type: 'WORKFLOW' | 'NODE';
};

export type Chunk = {
  event: ChunkEvent;
  events: ChunkEvent[];
  chat_id: string;
  chat_item_id: string;
  first_token_time: string;
  state: StreamingState;
  data: string;
};

export type FnCallValueType = {
  type: 'FUNCTION_CALL';
  value: {
    name: string;
    arguments: {};
    id: string;
  };
};
